import { useEffect, useState } from "react";
import { useAuthFetch } from "../../../hooks/useAuthFetch";
import { useParams } from "react-router-dom";
import { TextInput } from "../../../components/textInput";
import { TextArea } from "../../../components/textArea";
import { Toast } from "../../../components/toast";
import { SideSheet } from "../../../components/sheet/sideSheet";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CheckpointChecklist } from "./checklist";

const schema = z.object({
  checkpoint_name: z.string().min(1, "Checkpoint name is required"),
  checkpoint_description: z
    .string()
    .min(1, "Checkpoint description is required"),
  checkpoint_type: z.string().min(1, "Checkpoint type is required"),
  checklist_items: z
    .array(
      z.object({
        item_text: z
          .string()
          .min(5, "Checklist items require at least 5 characters.")
          .optional(),
      }),
    )
    .optional(),
});

function CheckpointDetails({
  isOpen,
  toggleOpen,
  setCheckpoints,
  fetchCheckpoints,
  checkpoints,
  selectedTourId,
  selectedCheckpointId,
  setSelectedTourCheckpoints,
  fetchSelectedCheckpointDetails,
  isAddMode,
  selectedCheckpointType,
}) {
  const params = useParams();
  const authFetch = useAuthFetch();

  const [existingChecklistItems, setExistingChecklistItems] = useState([]);
  const [checkPointTypes, setCheckPointTypes] = useState([]);

  const methods = useForm({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {
      checkpoint_name: "",
      checkpoint_description: "",
      checkpoint_type: "1",
      checklist_items: [{ item_id: null, item_text: "" }],
    },
  });

  const { register, handleSubmit, reset, getValues } = methods;

  /// Fetch checkpoint types once on mount ///
  useEffect(() => {
    authFetch(`${process.env.REACT_APP_API_URL}/v1/sites/types/checks`)
      .then((r) => r.json())
      .then((data) => setCheckPointTypes(data))
      .catch((err) => console.error("Error fetching checkpoint types:", err));
  }, []);

  /// Populate form when sheet opens ///
  useEffect(() => {
    if (isAddMode) {
      reset({
        checkpoint_name: "",
        checkpoint_description: "",
        checkpoint_type: "1",
        checklist_items: [{ item_id: null, item_text: "" }],
      });
      setExistingChecklistItems([]);
      return;
    }

    if (!selectedCheckpointId || !isOpen) return;

    // Single call — checklist_items is already included in the response
    authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/checkpoints/${selectedCheckpointId}`,
    )
      .then((r) => r.json())
      .then((data) => {
        const checklistData = data.checklist_items || [];
        setExistingChecklistItems(checklistData);
        reset({
          checkpoint_name: data.checkpoint_name || "",
          checkpoint_description: data.checkpoint_description || "",
          checkpoint_type: data.checkpoint_type?.toString() || "1",
          checklist_items: checklistData.map((item) => ({
            item_id: item.id,
            item_text: item.item_text,
          })),
        });
      })
      .catch((err) => console.error("Error fetching checkpoint details:", err));
  }, [selectedCheckpointId, isAddMode, isOpen]);

  /// Toast ///
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const handleResponse = (message, type) =>
    setToast({ show: true, message, type });
  const hideToast = () => setToast((prev) => ({ ...prev, show: false }));

  /// Submit ///
  const onSubmit = (data) =>
    isAddMode ? createCheckpoint(data) : updateCheckpoint(data);
  const handleSave = handleSubmit(onSubmit);
  const onError = (errors) => console.log("form error", errors);

  const updateCheckpoint = (data) => {
    authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/checkpoints/${selectedCheckpointId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    )
      .then((r) => {
        if (!r.ok) throw new Error(`Fetch returned status ${r.status}`);
        return r.json();
      })
      .then(() => {
        fetchCheckpoints();
        handleResponse("Checkpoint updated successfully", "success");
      })
      .catch((err) => {
        console.error("Error:", err);
        handleResponse("Failed to update checkpoint", "danger");
      });
  };

  const createCheckpoint = (data) => {
    if (selectedTourId) data.tourId = selectedTourId;
    data.position = checkpoints.length + 1;

    authFetch(
      `${process.env.REACT_APP_API_URL}/v1/sites/${params.id}/checkpoints/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
    )
      .then(async (r) => {
        if (!r.ok) {
          let msg = "Failed to create checkpoint";
          try {
            const e = await r.json();
            msg = e.error || msg;
          } catch (_) {}
          throw new Error(msg);
        }
        return r.json();
      })
      .then((result) => {
        const typeId = parseInt(data.checkpoint_type, 10);
        const selectedType = checkPointTypes.find(
          (t) => parseInt(t.id, 10) === typeId,
        );
        const newCheckpoint = {
          id: result.checkpointId,
          checkpoint_name: data.checkpoint_name,
          type_name: selectedType ? selectedType.type_name : "Unknown type",
          position: data.position,
          checkpoint_description: data.checkpoint_description,
        };
        setCheckpoints((prev) => [...prev, newCheckpoint]);
        setSelectedTourCheckpoints((prev) => [...prev, newCheckpoint]);
        fetchCheckpoints();
        reset();
        handleResponse("Checkpoint created successfully", "success");
      })
      .catch((err) => {
        console.error("Error:", err);
        handleResponse(err.message || "Failed to create checkpoint", "danger");
      });
  };

  return (
    <div>
      <FormProvider {...methods}>
        <SideSheet
          isOpen={isOpen}
          onClose={toggleOpen}
          title={!isAddMode ? "Checkpoint details" : "Create checkpoint"}
          isAddMode={isAddMode}
          footerText={isAddMode ? "Create checkpoint" : "Update checkpoint"}
          onError={onError}
          handleSave={handleSave}
        >
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
            <div className="space-y-3">
              <TextInput name="checkpoint_name" label="Checkpoint name" />
              <TextArea
                name="checkpoint_description"
                label="Checkpoint description"
              />
            </div>
            <div>
              <fieldset>
                <legend className="pb-2 text-sm font-medium leading-6 text-gray-900 dark:text-gray-200">
                  Type
                </legend>
                <div className="flex items-center mb-4">
                  {checkPointTypes.map((type) => (
                    <div
                      key={type.id}
                      className="flex mr-6 align-middle items-center"
                    >
                      <input
                        id={type.id}
                        type="radio"
                        className="focus:ring-brand-primary h-4 w-4 text-brand-primary border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                        value={type.id.toString()}
                        {...register("checkpoint_type")}
                      />
                      <div className="pl-2">
                        <label
                          htmlFor={type.id}
                          className="font-medium text-gray-900 dark:text-gray-200"
                        >
                          {type.type_name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
                <div>
                  <CheckpointChecklist label="Checklist" methods={methods} />
                </div>
              </fieldset>
            </div>
          </div>
        </SideSheet>
      </FormProvider>

      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onHide={hideToast}
      />
    </div>
  );
}

export { CheckpointDetails };
